/**
 * DRBFM Input Step Component
 *
 * Wizard step for entering DRBFM analysis input data:
 * - Item name (required)
 * - Analysis title (optional)
 * - Old specification (required) - via document selection or text input
 * - New specification (required) - via document selection or text input
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Help as HelpIcon,
  Description as DescriptionIcon,
  CompareArrows as CompareArrowsIcon,
  Folder as FolderIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DRBFMInputData } from '../../../types';
import { CADModelSelect } from '../../cad/CADModelSelect';
import { DocumentSelector } from '../DocumentSelector';
import { parsePartLabelsJson } from '../../../utils/cadPartLabels';

type InputMode = 'document' | 'text';

interface DRBFMInputStepProps {
  inputData: DRBFMInputData;
  onInputChange: (data: Partial<DRBFMInputData>) => void;
  onStartAnalysis: () => void;
  canStart: boolean;
  isLoading: boolean;
  cadEnabled?: boolean | null;
}

const DRBFMInputStep: React.FC<DRBFMInputStepProps> = ({
  inputData,
  onInputChange,
  onStartAnalysis,
  canStart,
  isLoading,
  cadEnabled = null,
}) => {
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [inputMode, setInputMode] = useState<InputMode>('document');
  const [partLabelsJson, setPartLabelsJson] = useState('');
  const [partLabelsError, setPartLabelsError] = useState<string | null>(null);

  // State for selected document info (for display)
  const [oldDocInfo, setOldDocInfo] = useState<{ fileName: string; preview: string } | null>(null);
  const [newDocInfo, setNewDocInfo] = useState<{ fileName: string; preview: string } | null>(null);

  // Handle text input changes
  const handleChange = (field: keyof DRBFMInputData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    onInputChange({ [field]: value });

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle input mode change
  const handleModeChange = (_event: React.SyntheticEvent, newMode: InputMode) => {
    setInputMode(newMode);
  };

  // Handle old specification document selection
  const handleOldDocSelect = (documentId: string, content: string, fileName: string, _title: string) => {
    onInputChange({
      specOldDocumentId: documentId,
      oldSpecification: content,
    });
    setOldDocInfo({ fileName, preview: content });
    // Clear validation error
    if (validationErrors.oldSpecification) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.oldSpecification;
        return newErrors;
      });
    }
  };

  // Handle old specification document clear
  const handleOldDocClear = () => {
    onInputChange({
      specOldDocumentId: undefined,
      oldSpecification: '',
    });
    setOldDocInfo(null);
  };

  // Handle new specification document selection
  const handleNewDocSelect = (documentId: string, content: string, fileName: string, _title: string) => {
    onInputChange({
      specNewDocumentId: documentId,
      newSpecification: content,
    });
    setNewDocInfo({ fileName, preview: content });
    // Clear validation error
    if (validationErrors.newSpecification) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.newSpecification;
        return newErrors;
      });
    }
  };

  // Handle new specification document clear
  const handleNewDocClear = () => {
    onInputChange({
      specNewDocumentId: undefined,
      newSpecification: '',
    });
    setNewDocInfo(null);
  };

  const handlePartLabelsChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setPartLabelsJson(value);

    try {
      const labels = parsePartLabelsJson(value);
      setPartLabelsError(null);
      onInputChange({ predefinedPartLabels: labels.length > 0 ? labels : undefined });
    } catch (err) {
      setPartLabelsError(err instanceof Error ? err.message : t('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'));
    }
  };

  // Validate before starting analysis
  const handleStartClick = () => {
    const errors: Record<string, string> = {};

    if (!inputData.itemName.trim()) {
      errors.itemName = t('drbfm.errors.itemNameRequired');
    }

    // Validate old specification: either document ID or text content
    if (!inputData.oldSpecification.trim() && !inputData.specOldDocumentId) {
      errors.oldSpecification = t('drbfm.errors.oldSpecRequired');
    }

    // Validate new specification: either document ID or text content
    if (!inputData.newSpecification.trim() && !inputData.specNewDocumentId) {
      errors.newSpecification = t('drbfm.errors.newSpecRequired');
    }

    if (partLabelsError) {
      errors.predefinedPartLabels = partLabelsError;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onStartAnalysis();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('drbfm.input.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('drbfm.input.description')}
        </Typography>
      </Box>

      {/* Input Form */}
      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="primary" />
                {t('drbfm.input.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('drbfm.input.itemName')}
                    value={inputData.itemName}
                    onChange={handleChange('itemName')}
                    fullWidth
                    required
                    error={!!validationErrors.itemName}
                    helperText={validationErrors.itemName || t('drbfm.input.itemNameHelper')}
                    placeholder={t('drbfm.input.itemNamePlaceholder')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label={t('drbfm.input.analysisTitle')}
                    value={inputData.title}
                    onChange={handleChange('title')}
                    fullWidth
                    helperText={t('drbfm.input.analysisTitleHelper')}
                    placeholder={t('drbfm.input.analysisTitlePlaceholder')}
                  />
                </Grid>
                {cadEnabled !== false && (
                  <Grid size={{ xs: 12 }}>
                    <CADModelSelect
                      value={inputData.cadModelId || ''}
                      onChange={(cadModelId) => onInputChange({ cadModelId })}
                      disabled={isLoading}
                      helperText={t('drbfm.cadLink.inputHelper', 'CADモデルを選ぶと、DRBFM解析完了後に3D上の該当部位へ自動リンクします')}
                    />
                  </Grid>
                )}
                {cadEnabled !== false && inputData.cadModelId && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('drbfm.cadLink.partLabelsJson', '部位ラベルJSON')}
                      value={partLabelsJson}
                      onChange={handlePartLabelsChange}
                      fullWidth
                      multiline
                      minRows={3}
                      disabled={isLoading}
                      error={!!partLabelsError || !!validationErrors.predefinedPartLabels}
                      helperText={
                        partLabelsError
                        || validationErrors.predefinedPartLabels
                        || t('drbfm.cadLink.partLabelsHelper', 'cover_part_labels.json全体、またはparts配列を貼り付けると優先ラベルとして使います')
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Specifications */}
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CompareArrowsIcon color="primary" />
                <Typography variant="subtitle1">
                  {t('drbfm.input.specifications')}
                </Typography>
                <Tooltip title={t('drbfm.input.specificationsHelp')}>
                  <IconButton size="small">
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Input Mode Tabs */}
              <Tabs
                value={inputMode}
                onChange={handleModeChange}
                sx={{ mb: 2 }}
                variant="fullWidth"
              >
                <Tab
                  value="document"
                  label={t('drbfm.input.documentMode', 'ドキュメント選択')}
                  icon={<FolderIcon />}
                  iconPosition="start"
                />
                <Tab
                  value="text"
                  label={t('drbfm.input.textMode', 'テキスト入力')}
                  icon={<EditIcon />}
                  iconPosition="start"
                />
              </Tabs>

              <Grid container spacing={2}>
                {inputMode === 'document' ? (
                  <>
                    {/* Document Selection Mode - Old Specification */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        {t('drbfm.input.oldSpecification')}
                      </Typography>
                      <DocumentSelector
                        label={t('drbfm.input.selectOldSpec', '旧仕様書を選択')}
                        selectedDocumentId={inputData.specOldDocumentId}
                        selectedFileName={oldDocInfo?.fileName}
                        contentPreview={oldDocInfo?.preview}
                        onSelect={handleOldDocSelect}
                        onClear={handleOldDocClear}
                        error={validationErrors.oldSpecification}
                        disabled={isLoading}
                      />
                    </Grid>

                    {/* Document Selection Mode - New Specification */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        {t('drbfm.input.newSpecification')}
                      </Typography>
                      <DocumentSelector
                        label={t('drbfm.input.selectNewSpec', '新仕様書を選択')}
                        selectedDocumentId={inputData.specNewDocumentId}
                        selectedFileName={newDocInfo?.fileName}
                        contentPreview={newDocInfo?.preview}
                        onSelect={handleNewDocSelect}
                        onClear={handleNewDocClear}
                        error={validationErrors.newSpecification}
                        disabled={isLoading}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* Text Input Mode - Old Specification */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        {t('drbfm.input.oldSpecification')}
                      </Typography>
                      <TextField
                        value={inputData.oldSpecification}
                        onChange={handleChange('oldSpecification')}
                        fullWidth
                        required
                        multiline
                        rows={12}
                        error={!!validationErrors.oldSpecification}
                        helperText={validationErrors.oldSpecification}
                        placeholder={t('drbfm.input.oldSpecPlaceholder')}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    </Grid>

                    {/* Text Input Mode - New Specification */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        {t('drbfm.input.newSpecification')}
                      </Typography>
                      <TextField
                        value={inputData.newSpecification}
                        onChange={handleChange('newSpecification')}
                        fullWidth
                        required
                        multiline
                        rows={12}
                        error={!!validationErrors.newSpecification}
                        helperText={validationErrors.newSpecification}
                        placeholder={t('drbfm.input.newSpecPlaceholder')}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Info Alert */}
        <Grid size={{ xs: 12 }}>
          <Alert severity="info">
            {t('drbfm.input.analysisInfo')}
          </Alert>
        </Grid>

        {/* Start Button */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartClick}
              disabled={!canStart || isLoading}
            >
              {isLoading ? t('drbfm.input.starting') : t('drbfm.input.startAnalysis')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DRBFMInputStep;
